import { lazy, Suspense } from "react";
import { ReactLenis } from "lenis/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useHotjar } from "@/hooks/useHotjar";
import { useAlooChat } from "@/hooks/useAlooChat";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Index from "./pages/Index";
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import StartProject from "./pages/StartProject";
import Careers from "./pages/Careers";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";
import ServicePage from "./pages/ServicePage";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import SolutionsIndex from "./pages/SolutionsIndex";
import SolutionDetail from "./pages/SolutionDetail";
import WorkIndex from "./pages/WorkIndex";
import WorkDetail from "./pages/WorkDetail";
import FormPage from "./pages/FormPage";
import { PortfolioRedirect } from "./components/PortfolioRedirect";
import EcommercePage from "./pages/EcommercePage";
import EcommerceIntegrations from "./pages/EcommerceIntegrations";
import WebPage from "./pages/WebPage";
import AIPage from "./pages/AIPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import { RoleProvider } from "./hooks/useUserRole";

const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminPosts = lazy(() => import("./pages/admin/AdminPosts"));
const AdminPostEditor = lazy(() => import("./pages/admin/AdminPostEditor"));
const AdminSolutions = lazy(() => import("./pages/admin/AdminSolutions"));
const AdminSolutionEditor = lazy(() => import("./pages/admin/AdminSolutionEditor"));
const AdminWork = lazy(() => import("./pages/admin/AdminWork"));
const AdminWorkEditor = lazy(() => import("./pages/admin/AdminWorkEditor"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminMenus = lazy(() => import("./pages/admin/AdminMenus"));
const AdminPages = lazy(() => import("./pages/admin/AdminPages"));
const AdminPageEditor = lazy(() => import("./pages/admin/AdminPageEditor"));
const AdminForms = lazy(() => import("./pages/admin/AdminForms"));
const AdminFormEditor = lazy(() => import("./pages/admin/AdminFormEditor"));
const AdminFormSubmissions = lazy(() => import("./pages/admin/AdminFormSubmissions"));
const AdminAISettings = lazy(() => import("./pages/admin/AdminAISettings"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));

function RouteLoadingFallback() {
  return <div className="min-h-screen bg-background" aria-busy="true" />;
}

function AdminElement({ children }: { children: JSX.Element }) {
  return <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>;
}

/** Public route definitions - shared between EN (no prefix) and AR (/ar prefix) */
function PublicRoutes() {
  return (
    <>
      <Route index element={<Index />} />
      <Route path="about" element={<About />} />
      <Route path="services" element={<Services />} />
      <Route path="services/:slug" element={<ServiceDetailPage />} />
      <Route path="contact" element={<Contact />} />
      <Route path="start-a-project" element={<StartProject />} />
      <Route path="careers" element={<Careers />} />
      <Route path="ai" element={<AIPage />} />
      <Route path="ai/ai-agents-automation" element={<ServicePage />} />
      <Route path="ai/ai-content-generation" element={<ServicePage />} />
      <Route path="ai/ai-chatbots-assistants" element={<ServicePage />} />
      <Route path="ai/ai-integrations" element={<ServicePage />} />
      <Route path="ai/knowledge-base-automation" element={<ServicePage />} />
      <Route path="ai/custom-ai-tools" element={<ServicePage />} />
      <Route path="ecommerce" element={<EcommercePage />} />
      <Route path="ecommerce/integrations-directory" element={<EcommerceIntegrations />} />
      <Route path="ecommerce/shopify-store-design-development" element={<ServicePage />} />
      <Route path="ecommerce/shopify-plus-solutions" element={<ServicePage />} />
      <Route path="ecommerce/ecommerce-ux-ui-design" element={<ServicePage />} />
      <Route path="ecommerce/store-migrations" element={<ServicePage />} />
      <Route path="ecommerce/payment-logistics-integrations" element={<ServicePage />} />
      <Route path="ecommerce/ecommerce-optimisation-growth" element={<ServicePage />} />
      <Route path="ecommerce/custom-store-projects" element={<ServicePage />} />
      <Route path="ecommerce/shopify-migrations" element={<ServicePage />} />
      <Route path="ecommerce/shopify-app-development" element={<ServicePage />} />
      <Route path="ecommerce/integrations" element={<ServicePage />} />
      <Route path="services/shopify-app-integrations" element={<ServicePage />} />
      <Route path="services/shopify-support-maintenance" element={<ServicePage />} />
      <Route path="web" element={<WebPage />} />
      <Route path="web/webflow-website-development" element={<ServicePage />} />
      <Route path="web/corporate-websites" element={<ServicePage />} />
      <Route path="web/landing-pages" element={<ServicePage />} />
      <Route path="web/multilingual-websites" element={<ServicePage />} />
      <Route path="web/headless-cms-solutions" element={<ServicePage />} />
      <Route path="web/website-redesign-optimisation" element={<ServicePage />} />
      <Route path="b2b" element={<ServicePage />} />
      <Route path="subscriptions" element={<ServicePage />} />
      <Route path="privacy-policy" element={<PrivacyPolicy />} />
      <Route path="terms" element={<Terms />} />
      <Route path="shopify" element={<ServicePage />} />
      <Route path="wordpress" element={<ServicePage />} />
      <Route path="wordpress/custom-wordpress-development" element={<ServicePage />} />
      <Route path="wordpress/corporate-websites" element={<ServicePage />} />
      <Route path="wordpress/cms-customisation" element={<ServicePage />} />
      <Route path="wordpress/plugin-integrations" element={<ServicePage />} />
      <Route path="wordpress/seo-content-strategy" element={<ServicePage />} />
      <Route path="wordpress/website-maintenance" element={<ServicePage />} />
      <Route path="woocommerce" element={<ServicePage />} />
      <Route path="woocommerce/ecommerce-store-setup" element={<ServicePage />} />
      <Route path="woocommerce/woocommerce-customisation" element={<ServicePage />} />
      <Route path="woocommerce/payment-shipping-integrations" element={<ServicePage />} />
      <Route path="woocommerce/store-migration" element={<ServicePage />} />
      <Route path="woocommerce/performance-optimisation" element={<ServicePage />} />
      <Route path="woocommerce/security-maintenance" element={<ServicePage />} />
      <Route path="webflow" element={<ServicePage />} />
      <Route path="webflow/website-design-development" element={<ServicePage />} />
      <Route path="webflow/cms-setup-configuration" element={<ServicePage />} />
      <Route path="webflow/landing-pages" element={<ServicePage />} />
      <Route path="webflow/performance-optimisation" element={<ServicePage />} />
      <Route path="webflow/multilingual-websites" element={<ServicePage />} />
      <Route path="webflow/support-maintenance" element={<ServicePage />} />
      <Route path="lovable" element={<ServicePage />} />
      <Route path="lovable/ai-product-development" element={<ServicePage />} />
      <Route path="lovable/saas-prototyping" element={<ServicePage />} />
      <Route path="lovable/ai-agents-automation" element={<ServicePage />} />
      <Route path="lovable/internal-tools-dashboards" element={<ServicePage />} />
      <Route path="lovable/workflow-integrations" element={<ServicePage />} />
      <Route path="lovable/development-support" element={<ServicePage />} />
      <Route path="blog" element={<BlogIndex />} />
      <Route path="blog/:slug" element={<BlogPost />} />
      <Route path="forms/:slug" element={<FormPage />} />
      <Route path="solutions" element={<SolutionsIndex />} />
      <Route path="solutions/:slug" element={<SolutionDetail />} />
      <Route path="work" element={<WorkIndex />} />
      <Route path="work/:slug" element={<WorkDetail />} />
      <Route path="portfolio" element={<PortfolioRedirect />} />
      <Route path="portfolio/:slug" element={<PortfolioRedirect />} />
    </>
  );
}

function AppRoutes() {
  usePageTracking();
  useHotjar();
  useAlooChat();

  return (
    <Routes>
      <Route path="/">{PublicRoutes()}</Route>
      <Route path="/ar">{PublicRoutes()}</Route>

      <Route path="/admin/login" element={<AdminElement><AdminLogin /></AdminElement>} />
      <Route path="/auth/login" element={<AdminElement><AdminLogin /></AdminElement>} />
      <Route path="/admin" element={<AdminElement><AdminDashboard /></AdminElement>} />
      <Route path="/admin/dashboard" element={<AdminElement><AdminDashboard /></AdminElement>} />
      <Route path="/admin/blog" element={<AdminElement><AdminPosts /></AdminElement>} />
      <Route path="/admin/blog/:id" element={<AdminElement><AdminPostEditor /></AdminElement>} />
      <Route path="/admin/posts/:id" element={<AdminElement><AdminPostEditor /></AdminElement>} />
      <Route path="/admin/solutions" element={<AdminElement><AdminSolutions /></AdminElement>} />
      <Route path="/admin/solutions/:id" element={<AdminElement><AdminSolutionEditor /></AdminElement>} />
      <Route path="/admin/work" element={<AdminElement><AdminWork /></AdminElement>} />
      <Route path="/admin/work/:id" element={<AdminElement><AdminWorkEditor /></AdminElement>} />
      <Route path="/admin/analytics" element={<AdminElement><AdminAnalytics /></AdminElement>} />
      <Route path="/admin/users" element={<AdminElement><AdminUsers /></AdminElement>} />
      <Route path="/admin/users/activity" element={<AdminElement><AdminAuditLogs /></AdminElement>} />
      <Route path="/admin/menus" element={<AdminElement><AdminMenus /></AdminElement>} />
      <Route path="/admin/pages" element={<AdminElement><AdminPages /></AdminElement>} />
      <Route path="/admin/pages/:id" element={<AdminElement><AdminPageEditor /></AdminElement>} />
      <Route path="/admin/forms" element={<AdminElement><AdminForms /></AdminElement>} />
      <Route path="/admin/forms/new" element={<AdminElement><AdminFormEditor /></AdminElement>} />
      <Route path="/admin/forms/submissions" element={<AdminElement><AdminFormSubmissions /></AdminElement>} />
      <Route path="/admin/forms/:id/edit" element={<AdminElement><AdminFormEditor /></AdminElement>} />
      <Route path="/admin/testimonials" element={<AdminElement><AdminTestimonials /></AdminElement>} />
      <Route path="/admin/settings" element={<Navigate to="/admin/settings/ai" replace />} />
      <Route path="/admin/settings/ai" element={<AdminElement><AdminAISettings /></AdminElement>} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ReactLenis
          root
          options={{
            duration: 0.7,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
            wheelMultiplier: 1.2,
          }}
        >
          <RoleProvider>
            <LanguageProvider>
              <ScrollToTop />
              <AppRoutes />
            </LanguageProvider>
          </RoleProvider>
        </ReactLenis>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;